import { atom } from "jotai";
import * as z from "zod";
import { newAssetNameAtom } from "./newAssetNameAtom";
import { extractErrorMessageFromResponse } from "../utils";
import { getSubpathBridgeClientFetch } from "@zapier/identity/client/fetch";
import {
  createTableResponseSchema,
  createRecordsResponseSchema,
} from "./schemas/tablesApiSchemas";

const tablesClient = getSubpathBridgeClientFetch("tables");

export type CreateTableResponse = z.infer<typeof createTableResponseSchema>;

/**
 * Creates a new blank record in the specified table.
 * This operation is non-blocking - log errors but don't fail.
 * @param tableId - The ID of the table to create the record in.
 * @returns The created record data or null if the record creation fails.
 */
async function createNewTableRecord(
  tableId: string,
): Promise<z.infer<typeof createRecordsResponseSchema> | null> {
  try {
    const createNewRecordResponse: unknown = await tablesClient(
      `/v1/tables/${tableId}/records`,
      {
        method: "POST",
        body: JSON.stringify({
          new_records: [{ data: {} }],
        }),
      },
    );

    if (!(createNewRecordResponse instanceof Response)) {
      console.error(
        `Unknown response type from bridge client for creating record in table ${tableId}`,
      );
      return null;
    }

    if (createNewRecordResponse.ok) {
      const recordsJson: unknown = await createNewRecordResponse.json();
      const parsedResponse = createRecordsResponseSchema.parse(recordsJson);
      return parsedResponse;
    } else {
      /* ----- Record creation failed – log & continue ----- */
      const errorMessage = await extractErrorMessageFromResponse(
        createNewRecordResponse,
      );
      console.error(
        `Failed to create initial record for table id ${tableId}: ${errorMessage}`,
      );
      return null;
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const prettyError = z.prettifyError(error);
      console.error(
        `Create records API response validation failed:`,
        prettyError,
      );
    } else {
      console.error(`Error creating initial record:`, error);
    }
    return null;
  }
}

const initialTableBody = (name: string) => {
  return {
    table: {
      name,
      kind: "table",
    },
    fields: [
      {
        config: {},
        name: "Click this to run a Zap",
        options: {
          button: {
            label: "",
            is_enabled: true,
          },
          icon: "ratingThumbsUp",
          alignment: "center",
        },
        type: "button_trigger_zap",
      },
      {
        config: {},
        name: "Field 1",
        options: {
          icon: "formText",
          alignment: "left",
        },
        type: "string",
      },
      {
        config: {},
        name: "Field 2",
        options: {
          icon: "formText",
          alignment: "left",
        },
        type: "string",
      },
      {
        config: {},
        name: "Field 3",
        options: {
          icon: "formText",
          alignment: "left",
        },
        type: "string",
      },
    ],
  };
};

export const shouldCreateNewTableAtom = atom<boolean>(false);
shouldCreateNewTableAtom.debugLabel = "shouldCreateNewTableAtom";

/**
 * Creates a new table with fields and initial record.
 * @param get - The atom getter function.
 * @returns The created table or null if the table creation fails.
 * Note: Record creation is handled separately via createNewTableRecord helper function.
 */

export const createNewTableAtom_SUSPENDS = atom(async (get) => {
  const shouldCreateNewTable = get(shouldCreateNewTableAtom);

  if (!shouldCreateNewTable) {
    return null;
  }

  const name = get(newAssetNameAtom);

  const body = initialTableBody(name);

  try {
    const res: unknown = await tablesClient(`/v1/tables-with-fields`, {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (!(res instanceof Response)) {
      console.error(`Unknown response type from bridge client for tables`);
      return null;
    }

    if (res.ok) {
      const json: unknown = await res.json();
      const createTableResponse = createTableResponseSchema.parse(json);
      const table = createTableResponse.data.table;

      // Create initial record in the newly created table
      // This operation is non-blocking - errors are handled and logged in createNewTableRecord
      await createNewTableRecord(table.id);

      return {
        ...table,
        owner: table.owner_zapier_customuser_id,
      };
    } else {
      /* ----- 5xx or other unexpected errors – log & return null ----- */
      const errorMessage = await extractErrorMessageFromResponse(res);
      console.error(`Failed to create table: ${errorMessage}`);
      return null;
    }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const prettyError = z.prettifyError(error);
      console.error(
        `Create table API response validation failed:`,
        prettyError,
      );
    } else {
      // Network errors, JSON parsing errors, or other unexpected errors
      console.error(`Error creating table:`, error);
    }

    return null;
  }
});

createNewTableAtom_SUSPENDS.debugLabel = "createNewTableAtom_SUSPENDS";
